import React, { useState, useEffect, useContext } from 'react';
import { AdminAuthContext } from '../../AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiEye, FiEyeOff, FiKey, FiPlus, FiSearch, FiTrash2, FiUserCheck, FiUsers } from 'react-icons/fi';
import './AdminUI.css';

const UserAccounts = () => {
  const { admin, loading } = useContext(AdminAuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', permission: 'READ' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!loading && (!admin || admin.role !== 'admin')) navigate('/admin-login', { replace: true });
  }, [admin, loading, navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch('https://valleyapi-dr9y.onrender.com/api/admin/admin-users', {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        });
        const text = await response.text();
        const data = JSON.parse(text);
        if (response.ok) setUsers(data);
        else setError(data.error || 'Administrative users could not be loaded');
      } catch (fetchError) {
        console.error('Error loading admin users:', fetchError.message);
        setError('Administrative users could not be loaded. Please try again.');
      }
    };
    if (!loading && admin) fetchUsers();
  }, [loading, admin]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const response = await fetch('https://valleyapi-dr9y.onrender.com/api/admin/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: JSON.stringify(formData),
      });
      const text = await response.text();
      const data = JSON.parse(text);
      if (response.ok) {
        setSuccess('Administrator account created successfully.');
        setUsers((current) => [...current, data.user]);
        setFormData({ email: '', password: '', name: '', permission: 'READ' });
      } else setError(data.error || 'The account could not be created');
    } catch (createError) {
      console.error('Error creating admin user:', createError.message);
      setError('The account could not be created. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this administrator account?')) return;
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`https://valleyapi-dr9y.onrender.com/api/admin/delete-user/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const text = await response.text();
      const data = JSON.parse(text);
      if (response.ok) {
        setSuccess('Administrator account deleted successfully.');
        setUsers((current) => current.filter((user) => user.id !== id));
      } else setError(data.error || 'The account could not be deleted');
    } catch (deleteError) {
      console.error('Error deleting admin user:', deleteError.message);
      setError('The account could not be deleted. Please try again.');
    }
  };

  if (loading) return <div className="va-page va-card va-loading"><div className="va-spinner" aria-label="Loading accounts" /></div>;

  const filteredUsers = users.filter((user) => `${user.name || ''} ${user.email || ''} ${user.permission || ''}`.toLowerCase().includes(searchTerm.toLowerCase()));
  return (
    <div className="va-page">
      <header className="va-page-header">
        <div>
          <span className="va-eyebrow">Access control</span>
          <h1 className="va-title">User accounts</h1>
          <p className="va-subtitle">Create administrator accounts and control their level of access to the portal.</p>
        </div>
      </header>


      {error && <div className="va-alert" role="alert"><FiAlertTriangle /> {error}</div>}
      {success && <div className="va-alert va-alert--success" role="status"><FiUserCheck /> {success}</div>}

      <div className="va-accounts-grid">
        <section className="va-card">
          <div className="va-card__header">
            <div><h2>Administrators</h2><p>People who can access the Valley admin portal.</p></div>
          </div>
          <div className="va-card__body" style={{ padding: 0 }}>
            <div className="va-toolbar" style={{ margin: 0, border: 0, borderBottom: '1px solid var(--va-line)', borderRadius: 0, boxShadow: 'none' }}>
              <div className="va-search"><FiSearch /><input type="search" placeholder="Search accounts" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} aria-label="Search administrator accounts" /></div>
              <span className="va-results">{filteredUsers.length} account{filteredUsers.length === 1 ? '' : 's'}</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="va-table">
                <thead><tr><th>User</th><th>Permission</th><th></th></tr></thead>
                <tbody>
                  {filteredUsers.length ? filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td><span className="va-table__primary">{user.name}</span><span className="va-table__secondary">{user.email}</span></td>
                      <td><span className={`va-badge ${user.permission === 'ALL' ? 'va-badge--success' : user.permission === 'WRITE' ? 'va-badge--info' : ''}`}>{user.permission}</span></td>
                      <td><div className="va-table__actions"><button className="va-button va-button--ghost va-button--icon" onClick={() => handleDelete(user.id)} title="Delete account" aria-label={`Delete ${user.name}`}><FiTrash2 /></button></div></td>
                    </tr>
                  )) : (
                    <tr><td colSpan="3"><div className="va-empty"><div><FiUsers /><strong>No accounts found</strong><p>No administrators match your search.</p></div></div></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="va-card">
          <div className="va-card__header"><div><h2>Create account</h2><p>Invite a new portal administrator.</p></div><span className="va-stat__icon"><FiPlus /></span></div>
          <div className="va-card__body">
            <form onSubmit={handleSubmit} className="va-form-grid va-form-grid--single">
              <div className="va-field"><label htmlFor="admin-name">Full name</label><input id="admin-name" className="va-input" type="text" name="name" value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} placeholder="e.g. Jordan Smith" required /></div>
              <div className="va-field"><label htmlFor="admin-account-email">Email address</label><input id="admin-account-email" className="va-input" type="email" name="email" value={formData.email} onChange={(event) => setFormData({ ...formData, email: event.target.value })} placeholder="name@company.com" required /></div>
              <div className="va-field"><label htmlFor="admin-account-password">Temporary password</label><div style={{ position: 'relative' }}><input id="admin-account-password" className="va-input" style={{ paddingRight: 42 }} type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={(event) => setFormData({ ...formData, password: event.target.value })} placeholder="Create a secure password" required /><button type="button" className="va-button va-button--ghost va-button--icon" style={{ position: 'absolute', right: 4, top: 4 }} onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <FiEyeOff /> : <FiEye />}</button></div></div>
              <div className="va-field"><label htmlFor="admin-permission">Permission level</label><select id="admin-permission" name="permission" value={formData.permission} onChange={(event) => setFormData({ ...formData, permission: event.target.value })} className="va-input"><option value="READ">Read only</option><option value="WRITE">Read and write</option><option value="ALL">Full access</option></select><small>Use full access only for trusted administrators.</small></div>
              <button className="va-button" type="submit" disabled={submitting}><FiKey /> {submitting ? 'Creating...' : 'Create account'}</button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

export default UserAccounts;
